/**
 * Authentication Wrapper Module
 * 
 * Provides centralized authentication state management and context for the entire application
 * Manages user login/logout, session state, and authenticated route rendering
 * 
 * Features:
 * - React Context API for global authentication state
 * - Login/logout functionality with localStorage persistence
 * - Protected route rendering based on authentication status
 * - Conditional header and navigation menu display
 * - Session-based user tracking
 * 
 * Context Provider:
 * - AuthContext: Contains isLoggedIn, user, login, logout
 * - Accessible via AuthData() hook throughout the application
 * 
 * Authentication Flow:
 * 1. User submits login credentials (username, password)
 * 2. login() function validates credentials
 * 3. On success: stores login state in localStorage, updates user state
 * 4. Authenticated routes render with header and navigation
 * 5. logout() clears all session data and redirects to logout page
 * 
 * Layout:
 * - Not logged in: Shows public routes only (login page)
 * - Logged in: Shows header, left navigation, and authenticated routes
 * 
 * Dependencies:
 * - React, React Router for routing and navigation
 * - config_ip_details.js: API_SERVER_URL configuration
 * - config_mapping.js: HTTP response codes
 * - server_utils.js: apiCall function for HTTP requests
 * - error_tracker.js: Error logging and tracking
 * - components/structure: Header, navigation, and route rendering components
 * - Bootstrap and custom CSS for styling
 * 
 * @component
 * @returns {JSX.Element} Provider component wrapping the entire application
 */

import React from "react";
import { createContext, useContext, useState } from "react"
import { RenderHeader } from "../components/structure/header";
import {API_SERVER_URL} from "../utils/config_ip_details";
import {API_SUCCESS_RESPONSE_CODE} from "../utils/config_mapping"
import { RenderRoutes,RenderMenuLeft } from "../components/structure/rendernavigation";
import { useNavigate } from "react-router-dom";
import {apiCall} from "../utils/server_utils"
import {trackError} from "../utils/error_tracker"
import '../assets/css/bootstrap.min.css'
import '../assets/css/icons.min.css'
import '../assets/css/app.min.css'
import '../assets/css/custom.min.css'

/**
 * Authentication Context
 * Provides global access to authentication state and methods
 * Usage: const { isLoggedIn, user, login, logout } = AuthData();
 */
const AuthContext = createContext();

/**
 * Hook to access authentication context
 * Must be used within a component wrapped by AuthWrapper
 * 
 * @returns {Object} Authentication context containing:
 *                   - isLoggedIn {boolean}: Current login status
 *                   - user {Object}: Current user object with name and isAuthenticated
 *                   - login {Function}: Login function
 *                   - logout {Function}: Logout function
 * 
 * @throws Will error if used outside AuthWrapper context
 * 
 * @example
 * function MyComponent() {
 *   const { isLoggedIn, user, logout } = AuthData();
 *   return isLoggedIn ? <p>Hello {user.name}</p> : <p>Not logged in</p>;
 * }
 */
export const AuthData = () => useContext(AuthContext);

/**
 * AuthWrapper Component
 * Root authentication provider and layout manager
 * Wraps the entire application with authentication context
 * Conditionally renders header and navigation based on login status
 * 
 * State Management:
 * - isLoggedIn: Boolean flag from localStorage or state
 * - user: Object containing name and authentication status
 * 
 * Conditional Rendering:
 * - If logged in: Shows header, left menu, and authenticated routes
 * - If not logged in: Shows public routes only (login page)
 * 
 * @component
 * @returns {JSX.Element} Context provider with conditional layout and routes
 */
export const AuthWrapper = () => {
     /**
      * Track login state from localStorage or initial state
      * localStorage "login" key set to "1" on successful login
      */
     const [isLoggedIn, setIsLoggedIn] = useState(
          !!localStorage.getItem("login")
      );
     
     /**
      * User state object
      * @type {Object}
      * @property {string} name - Username of logged-in user
      * @property {boolean} isAuthenticated - Authentication status flag
      */
     const [ user, setUser ] = useState({name: "", isAuthenticated: false})
     
     /**
      * React Router navigation hook for programmatic navigation
      */
     const navigate = useNavigate();
     
     /**
      * User login handler
      * Validates credentials and establishes user session
      * 
      * Current Implementation:
      * - Hardcoded password validation ("password")
      * - TODO: Replace with actual backend authentication API call
      * 
      * Process:
      * 1. Accepts username and password
      * 2. Validates password (currently hardcoded check)
      * 3. On success: Sets user state, stores login flag in localStorage
      * 4. Returns promise that resolves on success or rejects on failure
      * 
      * @param {string} userName - Username for login
      * @param {string} password - User password
      * @returns {Promise<string>} Resolves with "success" on valid credentials, rejects with error message
      * 
      * @example
      * const { login } = AuthData();
      * try {
      *   await login('john_doe', 'password');
      *   // User is now logged in
      * } catch (error) {
      *   console.error('Login failed:', error);
      * }
      */
     const login = (userName, password) => {
       
          // Make a call to the authentication API to check the username
          return new Promise((resolve, reject) => {
               if (password === "password") {
                    setUser({name: userName, isAuthenticated: true})
                    localStorage.setItem("login", "1");
                    localStorage.setItem("login_user_name", userName);
                    setIsLoggedIn(true);
                    resolve("success")
               } else {
                    reject("Incorrect password")
               }
          })
     }

     /**
      * Backend logout API call handler
      * Notifies backend that user is logging out
      * Clears session on server side
      * 
      * Process:
      * 1. Calls logout API endpoint on backend
      * 2. Validates successful API response
      * 3. Tracks any errors for debugging
      * 4. Continues logout process regardless of API response
      * 
      * @async
      * @returns {Promise<void>}
      * 
      * @note This function does NOT clear localStorage or redirect
      *       The logout() function handles those operations
      */
     const handleLogout = async (event) => {
     let apiUrl = API_SERVER_URL+ 'logout/'
     let method = "POST"
     try {
         const result = await apiCall(method,apiUrl);
         if(result!==undefined){
         if (result.code === API_SUCCESS_RESPONSE_CODE) {
           console.log("Logout")
         } else {
           trackError("The API response is invalid:<br/>URL:"+apiUrl+"<br/>Method:"+method+"<br>", JSON.stringify(result))
         }
         }
     } catch (error) {
       console.error("Error refreshing token:", error);
     }
     }
     
     /**
      * Complete user logout handler
      * Calls backend logout API, clears all session data, and redirects to logout page
      * 
      * Process:
      * 1. Calls handleLogout() to notify backend
      * 2. Clears all localStorage data
      * 3. Resets user authentication state
      * 4. Navigates to logout confirmation page
      * 
      * @returns {void}
      * 
      * @example
      * const { logout } = AuthData();
      * <button onClick={logout}>Logout</button>
      */
     const logout = () => {
          handleLogout()
          localStorage.clear(); 
          setIsLoggedIn(false);
          setUser({...user, isAuthenticated: false})
          navigate("/logout")
     }

     /**
      * AuthContext Provider with conditional layout
      * 
      * Context Value:
      * - isLoggedIn: Boolean authentication status
      * - user: Current user object
      * - login: Login function
      * - logout: Logout function
      * 
      * Conditional Rendering:
      * - If authenticated: Shows header + left navigation + routes
      * - If not authenticated: Shows routes only (login page)
      */
     return (
               <AuthContext.Provider value={{isLoggedIn,user, login, logout}}>
                    {isLoggedIn === true ? <>
                         {/* Header with user profile and settings */}
                         <RenderHeader />
                         {/* Left sidebar navigation menu filtered by user permissions */}
                         <RenderMenuLeft />
                         {/* Main content area with authenticated routes */}
                         <RenderRoutes />
                    </> : 
                    /* Show public routes (login page) when not authenticated */
                    <RenderRoutes />}
               </AuthContext.Provider>
     )
}